import { Injectable } from '@angular/core';
import { fromEvent as observableFromEvent, Observable } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';

export enum StorageType {
    LocalStorage = 'localStorage',
    SessionStorage = 'sessionStorage'
}

@Injectable({
    providedIn: 'root'
})
export class WindowService {
    private _innerWidth: number;

    constructor() {
        this._innerWidth = this.getWindow().innerWidth;
        this.getWindow().shellTokenRequestDictionary = {};
        this.setupInnerWidthListener();
    }

    public get innerWidth(): number {
        return this._innerWidth;
    }

    public get nativeWindow(): Window {
        return this.getWindow();
    }

    // In case where Zone.js gets overwritten, use this to reset it back.
    // Should be removed when: https://github.com/angular/zone.js/pull/899 gets merged in
    public ensureZoneJs(): void {
        try {
            Zone.assertZonePatched();
        } catch (ex) {
            (window as any).Promise = window.ZoneJSBackupPromise;
        }
    }

    public isStandalonePwa(): Observable<boolean> {
        return observableFromEvent(this.getWindow(), 'appinstalled').pipe(
            map(_event => true),
            startWith(this.nativeWindow.matchMedia('(display-mode: standalone)').matches)
        );
    }

    public getStorage(storage: StorageType): any {
        if (!this.isStorageAvailable(storage)) {
            return null;
        }
        switch (storage) {
            case StorageType.LocalStorage:
                return this.nativeWindow.localStorage;
            case StorageType.SessionStorage:
                return this.nativeWindow.sessionStorage;
            default:
                return null;
        }
    }

    public isStorageAvailable(type: StorageType): boolean {
        try {
            return !!window[type];
        } catch (e) {
            return false;
        }
    }

    private getWindow(): Window {
        return window;
    }

    private setupInnerWidthListener(): void {
        observableFromEvent(this.getWindow(), 'resize').pipe(debounceTime(10)).subscribe(
            () => this._innerWidth = this.getWindow().innerWidth
        );
    }
}
